import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MatrixContext } from './MatrixContext';
import { useI18n } from './I18nContext';
import matrixService from '../services/matrixService';
import draftStorageService from '../services/draftStorageService';
import LoadingOverlay from '../components/Shared/LoadingOverlay';
import logger from '../utils/logger';

/**
 * Provides a fully initialized and synced Matrix client to the application.
 *
 * This lean provider only provides the client instance - all state management
 * is handled by dedicated hooks for better separation of concerns.
 */
export const MatrixClientProvider = ({ children }) => {
  const { t } = useI18n();
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    logger.info('MatrixClientProvider: Initializing client service...');

    const initializeClient = async () => {
      try {
        // Initialize the Matrix service (which handles all the setup)
        await matrixService.initialize();
        
        // Start sync
        await matrixService.startSync();
        
        // Get the client instance
        const initializedClient = matrixService.getClient();
        
        if (isMounted && initializedClient) {
          logger.info('MatrixClientProvider: Client initialized and ready.');
          setClient(initializedClient);
          setIsLoading(false);
          
          // Cleanup old drafts (>7 days)
          const cleanedCount = draftStorageService.cleanupOldDrafts();
          if (cleanedCount > 0) {
            logger.info(`Cleaned up ${cleanedCount} old draft(s)`);
          }
        }
      } catch (err) {
        logger.error('MatrixClientProvider: Failed to initialize Matrix client:', err);
        if (isMounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    };

    initializeClient();

    return () => {
      isMounted = false;
      // Note: We don't stop the client here as it might be needed for logout
      // The client lifecycle is managed by matrixService
    };
  }, []);

  if (isLoading) {
    return <LoadingOverlay message={t('ScreenOverlay.loading')} />;
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}>
        <h2 style={{ color: '#d32f2f', marginBottom: '10px' }}>
          Failed to Initialize Session
        </h2>
        <p style={{ color: '#666', maxWidth: '500px' }}>
          {error.message}
        </p>
        <p style={{ color: '#999', marginTop: '20px' }}>
          Please try refreshing the page or logging in again.
        </p>
      </div>
    );
  }

  return (
    <MatrixContext.Provider value={client}>
      {children}
    </MatrixContext.Provider>
  );
};

MatrixClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
